/**
 * The shared 429 "queue is full" response every scan-start lane returns
 * (`POST /api/scans`, `/api/scans/policy`, `/api/lite-scan` — all three now
 * go through the backend's `submit()`, issue #773's `queue-at-capacity`
 * rejection), as one typed value and one friendly copy (issue #779's
 * ratified "One 429 mapping"). Before this, the dashboard's two start calls
 * and the marketing widget's own raw fetch each decided this differently:
 * one passed the backend's raw error text through unmodified, one hand-rolled
 * its own override string, and the marketing widget had no 429-specific
 * handling at all.
 */

export const QUEUE_BUSY_MESSAGE = 'The scanner is busy right now. Please try again in a minute.';

export interface QueueBusyRejection {
  type: 'queue-busy';
  /** Seconds the backend suggests waiting, when the response body carried one. */
  retryAfter?: number;
  message: string;
}

/**
 * Classify a scan-start response as the shared queue-busy rejection, or null
 * when it isn't one. Takes the status and the already-parsed body rather than
 * a `Response` — `.json()` can only be consumed once, and callers that also
 * need other fields off the same body own that parse themselves.
 */
export function classifyQueueBusy(status: number, body: unknown): QueueBusyRejection | null {
  if (status !== 429) return null;
  // The anonymous audit's per-IP daily cap (#1778) is a 429 too, but waiting a
  // minute will not clear it: its own message (until UTC midnight) stands.
  if (body && typeof body === 'object' && (body as { code?: unknown }).code === 'ANON_AUDIT_DAILY_LIMIT') return null;
  const rawRetryAfter = body && typeof body === 'object' ? (body as { retryAfter?: unknown }).retryAfter : undefined;
  return {
    type: 'queue-busy',
    retryAfter: typeof rawRetryAfter === 'number' ? rawRetryAfter : undefined,
    message: QUEUE_BUSY_MESSAGE,
  };
}
